import { z } from 'zod';

/**
 * Canonical id/name of the always-present default markup layer. Single source of
 * truth: the reducer seed (MarkupLayerContext), the read boundary
 * (normalizeMarkupRead), the OCG exporter (ocg-writer), and the sidecar
 * round-trip all key off these so a default-layer markup resolves to ONE
 * "Markups" OCG instead of two mismatched ones. Kept in this pure module so the
 * pdf-export path can import it without pulling in the React context module.
 */
export const DEFAULT_MARKUP_LAYER_ID = 'pmt-default-markups';
export const DEFAULT_MARKUP_LAYER_NAME = 'Markups';

/**
 * The authored-layer fields that round-trip through the `pmt-layers.json` sidecar
 * (and become a real OCG on export). Deliberately omits any runtime-only field --
 * everything persisted is a markup layer by construction.
 *
 * App-private persistence shape, so it is owned by zod (see
 * docs/decisions/2026-08-13-validation-ownership-boundary.md); the type is
 * DERIVED from the schema so shape and validation cannot drift. The read
 * boundary (`layersFromPdf`) safeParses each entry and drops the invalid ones.
 */
export const PersistedMarkupLayerSchema = z.object({
    id: z.string(),
    name: z.string(),
    visible: z.boolean(),
    locked: z.boolean(),
});
export type PersistedMarkupLayer = z.infer<typeof PersistedMarkupLayerSchema>;

export interface MarkupLayerOption {
    id: string;
    name: string;
    visible?: boolean;
}

/**
 * Which layer does an EXISTING markup belong to?
 *
 * `layer_id` is stamped with a real value at the read boundary
 * (`normalizeMarkupRead`), so the only case left here is a DANGLING reference: a
 * layer that was deleted, or an id from another document. Those resolve to the
 * default layer, matching what layer deletion does (reparent, never orphan).
 *
 * Deliberately does NOT consider the active layer. Doing so made an unassigned
 * markup appear to move between layers as the user changed the active one, and
 * let a count badge computed with a different fallback disagree with the list it
 * labelled. A markup's layer is a stored fact -- nothing about the current
 * session may change which layer it reports.
 */
export function resolveMarkupLayerId(
    layerId: string | undefined,
    layers: readonly MarkupLayerOption[],
): string | null {
    if (layerId && layers.some((layer) => layer.id === layerId)) {
        return layerId;
    }
    if (layers.some((layer) => layer.id === DEFAULT_MARKUP_LAYER_ID)) {
        return DEFAULT_MARKUP_LAYER_ID;
    }
    return layers[0]?.id ?? null;
}

export function resolveMarkupLayerName(
    layerId: string | undefined,
    layers: readonly MarkupLayerOption[],
): string {
    const resolvedId = resolveMarkupLayerId(layerId, layers);
    return layers.find((layer) => layer.id === resolvedId)?.name ?? '';
}
